import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { projectsAPI, reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Plus, Trash2, Loader2, Eye, EyeOff,
  CheckCircle, AlertTriangle, XCircle, Info, HelpCircle, ArrowRight, Users, Link2
} from 'lucide-react';
import { RISK_LEVELS, MILESTONE_STATUSES, STATUS_COLORS } from '@/lib/constants';
import { format, startOfWeek, endOfWeek, addWeeks, addDays, differenceInDays } from 'date-fns';
import TeamMemberSelect from '@/components/TeamMemberSelect';
import { toast } from 'sonner';

const ReportBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editReportId = searchParams.get('edit');
  const isEditMode = !!editReportId;
  
  const [project, setProject] = useState(null);
  const [lastReport, setLastReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [summaryError, setSummaryError] = useState(false);
  const [showTeamAlert, setShowTeamAlert] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Refs for auto-focus
  const statusSummaryRef = useRef(null);
  const accomplishmentsRef = useRef(null);
  const upcomingRef = useRef(null);
  const blockersRef = useRef(null);
  const kudosRef = useRef(null);
  const linksRef = useRef(null);

  // Auto-focus status summary on mount
  useEffect(() => {
    if (!loading) {
      // Use setTimeout to ensure DOM is ready
      const timer = setTimeout(() => {
        const statusSummary = document.querySelector('[data-testid="status-summary-input"]');
        if (statusSummary) {
          statusSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
          statusSummary.focus();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Handler for managing team members - saves draft before navigating
  const handleManageTeam = () => {
    // Save draft to localStorage before showing dialog
    if (formData) {
      localStorage.setItem('reportDraft', JSON.stringify({
        projectId,
        formData,
        timestamp: Date.now()
      }));
    }
    setShowTeamAlert(true);
  };

  // Calculate default period dates (current week)
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });

  const [formData, setFormData] = useState({
    period_start: format(weekStart, 'yyyy-MM-dd'),
    period_end: format(weekEnd, 'yyyy-MM-dd'),
    status: 'on_track',
    status_summary: '',
    // EVM toggle
    show_evm: true,
    // Predictive EVM - simplified input
    planned_percent: '',
    actual_percent: '',
    ac: '',
    budget_notes: '',
    // Agile - Enhancement 19: Default blank
    sprint_number: '',
    sprint_goal: '',
    sprint_goal_achieved: 'yes',
    points_planned: '',
    points_completed: '',
    points_remaining: '',
    sprint_day: '',
    // Common
    accomplishments: [{ description: '', owner: '' }],
    upcoming: [{ description: '', owner: '' }],
    blockers: [],
    decisions: [],
    milestone_updates: [],
    new_risks: [],
    risk_updates: [],  // Enhancement 17: Each risk can have notes
    notes: '',
    kudos: [],  // Enhancement 18: Kudos section
    retro: { went_well: [], needs_improvement: [], action_items: [] },
    attachments: []  // Report attachments/links
  });

  // Load draft on mount if it exists and matches this project
  useEffect(() => {
    if (loading) return; // Wait until project is loaded
    
    const savedDraft = localStorage.getItem('reportDraft');
    if (savedDraft && !isEditMode) {
      try {
        const draft = JSON.parse(savedDraft);
        // Only restore if for this project and less than 30 minutes old
        if (draft.projectId === projectId && Date.now() - draft.timestamp < 30 * 60 * 1000) {
          setFormData(prev => ({
            ...prev,
            ...draft.formData
          }));
          // Clear the draft after restoring
          localStorage.removeItem('reportDraft');
          // Show toast notification
          toast.success('Draft Restored', {
            description: 'Your previous work has been restored.'
          });
        } else {
          localStorage.removeItem('reportDraft');
        }
      } catch (e) {
        localStorage.removeItem('reportDraft');
      }
    }
    // Mark initial load as complete after a short delay
    setTimeout(() => setInitialLoadComplete(true), 500);
  }, [projectId, isEditMode, loading]);

  // Autosave every 30 seconds
  useEffect(() => {
    if (loading || !project || !initialLoadComplete) return;
    
    const autosaveInterval = setInterval(() => {
      localStorage.setItem('reportDraft', JSON.stringify({
        projectId,
        formData,
        timestamp: Date.now(),
        isAutosave: true
      }));
    }, 30000); // 30 seconds
    
    return () => clearInterval(autosaveInterval);
  }, [formData, projectId, loading, project, initialLoadComplete]);

  // Track unsaved changes
  useEffect(() => {
    if (!loading && project && initialLoadComplete) {
      setHasUnsavedChanges(true);
    }
  }, [formData, loading, project, initialLoadComplete]);

  // Browser tab close warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // State for navigation warning dialog
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);

  // Handle safe navigation with unsaved changes check
  const handleSafeNavigate = useCallback((path) => {
    if (hasUnsavedChanges) {
      setPendingNavigationPath(path);
      setShowNavigationWarning(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  useEffect(() => {
    loadProjectAndLastReport();
  }, [projectId, editReportId]);

  const loadProjectAndLastReport = async () => {
    try {
      const projectResponse = await projectsAPI.getOne(projectId);
      const projectData = projectResponse.data;
      setProject(projectData);
      
      // If in edit mode, load the existing report
      if (isEditMode && editReportId) {
        const editReportResponse = await reportsAPI.getOne(editReportId);
        const editReport = editReportResponse.data;
        setEditingReport(editReport);
        
        // Pre-fill form with existing report data
        setFormData(prev => ({
          ...prev,
          period_start: editReport.period_start ? format(new Date(editReport.period_start), 'yyyy-MM-dd') : prev.period_start,
          period_end: editReport.period_end ? format(new Date(editReport.period_end), 'yyyy-MM-dd') : prev.period_end,
          status: editReport.status || prev.status,
          status_summary: editReport.status_summary || '',
          planned_percent: editReport.planned_percent ? String(editReport.planned_percent) : '',
          actual_percent: editReport.actual_percent ? String(editReport.actual_percent) : '',
          ac: editReport.ac ? String(editReport.ac) : '',
          budget_notes: editReport.budget_notes || '',
          sprint_number: editReport.sprint_number ? String(editReport.sprint_number) : '',
          sprint_goal: editReport.sprint_goal || '',
          sprint_goal_achieved: editReport.sprint_goal_achieved || 'yes',
          points_planned: editReport.points_planned ? String(editReport.points_planned) : '',
          points_completed: editReport.points_completed ? String(editReport.points_completed) : '',
          points_remaining: editReport.points_remaining ? String(editReport.points_remaining) : '',
          sprint_day: editReport.sprint_day ? String(editReport.sprint_day) : '',
          accomplishments: editReport.accomplishments?.length > 0 ? editReport.accomplishments : [{ description: '', owner: '' }],
          upcoming: editReport.upcoming?.length > 0 ? editReport.upcoming : [{ description: '', due_date: '' }],
          blockers: editReport.blockers || [],
          decisions: editReport.decisions || [],
          notes: editReport.notes || '',
          kudos: Array.isArray(editReport.kudos) ? editReport.kudos : (editReport.kudos ? [{ person: '', reason: editReport.kudos }] : []),
          attachments: editReport.attachments || [],
          milestone_updates: projectData.milestones?.map(m => ({
            id: m.id,
            name: m.name,
            status: m.status,
            actual_date: m.actual_date || ''
          })) || [],
          risk_updates: projectData.risks?.filter(r => r.status === 'open').map(r => ({
            risk_id: r.id,
            description: r.description,
            probability: r.probability,
            impact: r.impact,
            notes: ''
          })) || [],
        }));
        setLoading(false);
        return;
      }
      
      const reportsResponse = await reportsAPI.getByProject(projectId);
      const reports = reportsResponse.data;
      
      let initialData = {
        milestone_updates: projectData.milestones?.map(m => ({
          id: m.id,
          name: m.name,
          status: m.status,
          actual_date: m.actual_date || ''
        })) || [],
        risk_updates: projectData.risks?.filter(r => r.status === 'open').map(r => ({
          risk_id: r.id,
          description: r.description,
          probability: r.probability,
          impact: r.impact,
          notes: ''
        })) || [],
        sprint_number: projectData.methodology === 'agile' ? '1' : '',
        // Default points_planned from project's initial_velocity for agile projects
        points_planned: projectData.methodology === 'agile' && projectData.initial_velocity 
          ? String(projectData.initial_velocity) : '',
        // BUG-01 FIX: Only sync actual_percent from manual_percent_complete when EVM is enabled
        actual_percent: (projectData.evm_enabled && projectData.manual_percent_complete)
          ? String(projectData.manual_percent_complete) : ''
      };

      if (reports.length > 0) {
        const last = reports[0];
        setLastReport(last);
        
        // Find the most recent report with EVM data for carry-over
        const lastWithEvm = reports.find(r => r.planned_percent !== null && r.planned_percent !== undefined);
        
        const lastEnd = new Date(last.period_end);
        const nextStart = addWeeks(lastEnd, 0);
        nextStart.setDate(nextStart.getDate() + 1);
        const nextEnd = addWeeks(nextStart, 1);
        nextEnd.setDate(nextEnd.getDate() - 1);
        
        const carriedBlockers = (last.blockers || [])
          .filter(b => !b.solved)
          .map(b => ({
            ...b,
            days_blocked: (b.days_blocked || 0) + 7,
            carried_over: true,
            solved: false
          }));
        
        const carriedDecisions = (last.decisions || [])
          .filter(d => !d.solved)
          .map(d => ({
            ...d,
            carried_over: true,
            solved: false
          }));

        // Use lastWithEvm for EVM metrics if available, otherwise use last
        const evmSource = lastWithEvm || last;
        
        initialData = {
          ...initialData,
          period_start: format(nextStart, 'yyyy-MM-dd'),
          period_end: format(nextEnd, 'yyyy-MM-dd'),
          status: last.status,
          // Carry over EVM metrics from the most recent report that has them
          planned_percent: evmSource.planned_percent ? String(evmSource.planned_percent) : '',
          // BUG-01 FIX: Only carry dashboard progress when EVM is enabled
          actual_percent: (projectData.evm_enabled && projectData.manual_percent_complete)
            ? String(projectData.manual_percent_complete) 
            : (evmSource.actual_percent ? String(evmSource.actual_percent) : ''),
          ac: evmSource.ac ? String(evmSource.ac) : '',
          sprint_number: last.sprint_number ? String(last.sprint_number + 1) : '1',
          points_planned: last.points_planned ? String(last.points_planned) : '',
          blockers: carriedBlockers,
          decisions: carriedDecisions,
          accomplishments: [{ description: '', owner: '' }],
          upcoming: [{ description: '', owner: '' }],
        };
      }
      
      setFormData(prev => ({ ...prev, ...initialData }));
      
      // Respect project-level settings
      if (projectData.evm_enabled !== undefined) {
        setFormData(prev => ({ ...prev, show_evm: projectData.evm_enabled }));
      }
      
      // Set progress bar visibility based on project.simple_mode
      setFormData(prev => ({
        ...prev,
        section_visibility: {
          ...prev.section_visibility,
          progress: projectData.simple_mode ?? true,
        }
      }));
    } catch (error) {
      console.error('Failed to load project:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updates = { [field]: value };
      
      // Auto-fill period_end to 7 days after period_start
      if (field === 'period_start' && value) {
        const startDate = new Date(value);
        const endDate = addDays(startDate, 6); // 7 days total (start + 6)
        updates.period_end = format(endDate, 'yyyy-MM-dd');
      }
      
      // BUG 5 FIX: Validate end date is not before start date
      if (field === 'period_end' && value && prev.period_start) {
        const startDate = new Date(prev.period_start);
        const endDate = new Date(value);
        if (endDate < startDate) {
          // Don't allow end date before start date - keep existing value
          return prev;
        }
      }
      
      return { ...prev, ...updates };
    });
    if (field === 'status_summary' && value) {
      setSummaryError(false);
    }
  };

  const addArrayItem = (field, template) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], template] }));
    
    // Auto-focus the newly added item after a brief delay
    setTimeout(() => {
      let ref;
      if (field === 'accomplishments') ref = accomplishmentsRef;
      else if (field === 'upcoming') ref = upcomingRef;
      else if (field === 'blockers') ref = blockersRef;
      else if (field === 'kudos') ref = kudosRef;
      
      if (ref?.current) {
        const inputs = ref.current.querySelectorAll('input, textarea');
        const lastInputGroup = Array.from(inputs).slice(-2); // Last 2 inputs (the new item)
        if (lastInputGroup[0]) {
          lastInputGroup[0].focus();
        }
      }
    }, 50);
  };

  const updateArrayItem = (field, index, key, value) => {
    setFormData(prev => {
      const updated = [...prev[field]];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, [field]: updated };
    });
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.status_summary) {
      setSummaryError(true);
      setError('Please provide a status summary');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const reportData = {
        project_id: projectId,
        period_start: formData.period_start,
        period_end: formData.period_end,
        status: formData.status,
        status_summary: formData.status_summary,
        planned_percent: formData.planned_percent ? parseFloat(formData.planned_percent) : null,
        actual_percent: formData.actual_percent ? parseFloat(formData.actual_percent) : null,
        ac: formData.ac ? parseFloat(formData.ac) : null,
        budget_notes: formData.budget_notes || null,
        sprint_number: formData.sprint_number ? parseInt(formData.sprint_number) : null,
        sprint_goal: formData.sprint_goal || null,
        sprint_goal_achieved: project?.methodology === 'agile' ? formData.sprint_goal_achieved : null,
        points_planned: formData.points_planned ? parseInt(formData.points_planned) : null,
        points_completed: formData.points_completed ? parseInt(formData.points_completed) : null,
        points_remaining: formData.points_remaining ? parseInt(formData.points_remaining) : null,
        sprint_day: formData.sprint_day ? parseInt(formData.sprint_day) : null,
        burndown_data: formData.sprint_day && formData.points_remaining ? 
          [{ day: parseInt(formData.sprint_day), remaining: parseInt(formData.points_remaining) }] : null,
        accomplishments: formData.accomplishments.filter(a => a.description),
        upcoming: formData.upcoming.filter(u => u.description),
        blockers: formData.blockers.filter(b => b.description && !b.solved),
        decisions: formData.decisions.filter(d => d.description && !d.solved),
        milestone_updates: formData.milestone_updates,
        new_risks: formData.new_risks?.filter(r => r.description) || [],
        risk_updates: formData.risk_updates,
        kudos: typeof formData.kudos === 'string' ? formData.kudos : (formData.kudos?.filter(k => k.person || k.reason) || []),
        notes: formData.notes || null,
        retro: project?.methodology === 'agile' ? formData.retro : null,
        show_evm: formData.show_evm,
        attachments: formData.attachments?.filter(a => a.label && a.url) || []
      };

      let response;
      if (isEditMode && editReportId) {
        // Update existing report
        response = await reportsAPI.update(editReportId, reportData);
        // Clear draft and reset unsaved changes on successful save
        localStorage.removeItem('reportDraft');
        setHasUnsavedChanges(false);
        navigate(`/reports/${editReportId}`);
      } else {
        // Create new report
        response = await reportsAPI.create(reportData);
        // Clear draft and reset unsaved changes on successful save
        localStorage.removeItem('reportDraft');
        setHasUnsavedChanges(false);
        navigate(`/reports/${response.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} report`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate live EVM preview
  const getEVMPreview = () => {
    if (!project?.bac || !formData.planned_percent || !formData.actual_percent || !formData.ac) {
      return null;
    }
    const bac = project.bac;
    const pv = bac * (parseFloat(formData.planned_percent) / 100);
    const ev = bac * (parseFloat(formData.actual_percent) / 100);
    const ac = parseFloat(formData.ac);
    
    const spi = pv ? (ev / pv) : null;
    const cpi = ac ? (ev / ac) : null;
    const eac = cpi ? (bac / cpi) : null;
    const vac = eac ? (bac - eac) : null;
    
    return { spi, cpi, eac, vac, pv, ev, ac };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-7xl">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!project) return null;

  const isPredictive = project.methodology === 'predictive';
  const evmPreview = isPredictive ? getEVMPreview() : null;
  const statusColors = STATUS_COLORS[formData.status];

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => handleSafeNavigate(`/projects/${projectId}`)} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Project
            </Button>
            <h1 className="text-2xl font-bold">{isEditMode ? 'Edit Status Report' : 'Create Status Report'}</h1>
            <p className="text-slate-600">{project.name}</p>
            {isEditMode && editingReport && (
              <p className="text-sm text-blue-600 mt-1">
                <Info className="inline h-4 w-4 mr-1" />
                Editing report from {format(new Date(editingReport.report_date), 'MMM d, yyyy')}
              </p>
            )}
            {!isEditMode && lastReport && (
              <p className="text-sm text-blue-600 mt-1">
                <Info className="inline h-4 w-4 mr-1" />
                Values pre-populated from last report ({format(new Date(lastReport.report_date), 'MMM d')})
              </p>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column: Form */}
            <div className="space-y-6">

              {/* Report Period */}
              <Card>
                <CardHeader>
                  <CardTitle>Report Period</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Period Start</Label>
                      <Input
                        type="date"
                        value={formData.period_start}
                        onChange={(e) => updateFormData('period_start', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Period End</Label>
                      <Input
                        type="date"
                        value={formData.period_end}
                        onChange={(e) => updateFormData('period_end', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Overall Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Overall Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Project Status *</Label>
                    <RadioGroup
                      value={formData.status}
                      onValueChange={(value) => updateFormData('status', value)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="on_track" id="on_track" />
                        <Label htmlFor="on_track" className="flex items-center gap-2 cursor-pointer">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          On Track
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="at_risk" id="at_risk" />
                        <Label htmlFor="at_risk" className="flex items-center gap-2 cursor-pointer">
                          <AlertTriangle className="h-4 w-4 text-yellow-600" />
                          At Risk
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="off_track" id="off_track" />
                        <Label htmlFor="off_track" className="flex items-center gap-2 cursor-pointer">
                          <XCircle className="h-4 w-4 text-red-600" />
                          Off Track
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status_summary" className={summaryError ? 'text-red-600' : ''}>
                      Status Summary *
                    </Label>
                    <Textarea
                      id="status_summary"
                      ref={statusSummaryRef}
                      value={formData.status_summary}
                      onChange={(e) => updateFormData('status_summary', e.target.value)}
                      placeholder="Briefly explain the current status..."
                      rows={3}
                      data-testid="status-summary-input"
                      className={summaryError ? 'border-red-500 focus:ring-red-500' : ''}
                    />
                    {summaryError && (
                      <p className="text-sm text-red-600">Status summary is required</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* EVM Metrics (Predictive) - only shown if EVM is enabled in project settings */}
              {isPredictive && project?.evm_enabled && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Earned Value Metrics</CardTitle>
                        <CardDescription>
                          BAC: ${project.bac?.toLocaleString()} — Enter percentages and we calculate the rest
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="show_evm" className="text-sm">Show in Report</Label>
                        <Switch
                          id="show_evm"
                          checked={formData.show_evm}
                          onCheckedChange={(checked) => updateFormData('show_evm', checked)}
                          className="data-[state=checked]:bg-blue-600"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="planned_percent">Planned % Complete</Label>
                          <div className="relative">
                            <Input
                              id="planned_percent"
                              type="number"
                              min="0"
                              max="100"
                              value={formData.planned_percent}
                              onChange={(e) => updateFormData('planned_percent', e.target.value)}
                              placeholder="0"
                              className="pr-8"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="actual_percent">Actual % Complete</Label>
                          <div className="relative">
                            <Input
                              id="actual_percent"
                              type="number"
                              min="0"
                              max="100"
                              value={formData.actual_percent}
                              onChange={(e) => updateFormData('actual_percent', e.target.value)}
                              placeholder="0"
                              className="pr-8"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ac">Actual Cost to Date</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                            <Input
                              id="ac"
                              type="number"
                              value={formData.ac}
                              onChange={(e) => updateFormData('ac', e.target.value)}
                            placeholder="0"
                            className="pl-7"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="budget_notes">Budget Notes</Label>
                      <Textarea
                        id="budget_notes"
                        value={formData.budget_notes}
                        onChange={(e) => updateFormData('budget_notes', e.target.value)}
                        placeholder="Any notes about budget..."
                        rows={2}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sprint Metrics (Agile) */}
              {!isPredictive && (
                <Card>
                  <CardHeader>
                    <CardTitle>Sprint Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sprint_number">Sprint Number</Label>
                        <Input
                          id="sprint_number"
                          type="number"
                          value={formData.sprint_number}
                          onChange={(e) => updateFormData('sprint_number', e.target.value)}
                          placeholder="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sprint_goal">Sprint Goal</Label>
                        <Input
                          id="sprint_goal"
                          value={formData.sprint_goal}
                          onChange={(e) => updateFormData('sprint_goal', e.target.value)}
                          placeholder="What was the sprint goal?"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="points_planned">Points Planned</Label>
                        <Input
                          id="points_planned"
                          type="number"
                          value={formData.points_planned}
                          onChange={(e) => updateFormData('points_planned', e.target.value)}
                          placeholder="52"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="points_completed">Points Completed</Label>
                        <Input
                          id="points_completed"
                          type="number"
                          value={formData.points_completed}
                          onChange={(e) => updateFormData('points_completed', e.target.value)}
                          placeholder="45"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="points_remaining">Remaining Backlog Size</Label>
                        <Input
                          id="points_remaining"
                          type="number"
                          value={formData.points_remaining}
                          onChange={(e) => updateFormData('points_remaining', e.target.value)}
                          placeholder="7"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Accomplishments */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Accomplishments This Period</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('accomplishments', { description: '', owner: '' })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" ref={accomplishmentsRef}>
                  {formData.accomplishments.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={item.description}
                          onChange={(e) => updateArrayItem('accomplishments', index, 'description', e.target.value)}
                          placeholder="What was accomplished?"
                          autoFocus={index === formData.accomplishments.length - 1 && !item.description}
                        />
                        <TeamMemberSelect
                          value={item.owner}
                          onChange={(val) => updateArrayItem('accomplishments', index, 'owner', val)}
                          teamMembers={project?.stakeholders || []}
                          placeholder="Owner/Team (optional)"
                          onManageTeam={handleManageTeam}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem('accomplishments', index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Upcoming */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Upcoming Next Period</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('upcoming', { description: '', owner: '' })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" ref={upcomingRef}>
                  {formData.upcoming.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={item.description}
                          onChange={(e) => updateArrayItem('upcoming', index, 'description', e.target.value)}
                          placeholder="What's planned?"
                          autoFocus={index === formData.upcoming.length - 1 && !item.description}
                        />
                        <TeamMemberSelect
                          value={item.owner || ''}
                          onChange={(val) => updateArrayItem('upcoming', index, 'owner', val)}
                          teamMembers={project?.stakeholders || []}
                          placeholder="Owner/Team (optional)"
                          onManageTeam={handleManageTeam}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeArrayItem('upcoming', index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Blockers */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Blockers / Impediments</CardTitle>
                      {formData.blockers.some(b => b.carried_over) && (
                        <CardDescription className="text-blue-600">
                          Carried over from last report - toggle &quot;Solved&quot; if resolved
                        </CardDescription>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('blockers', { 
                        description: '', 
                        owner: '', 
                        date_started: format(new Date(), 'yyyy-MM-dd'),
                        escalated: false, 
                        solved: false 
                      })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" ref={blockersRef}>
                  {formData.blockers.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No blockers - great!</p>
                  ) : (
                    formData.blockers.map((item, index) => {
                      // Calculate days blocked from date_started
                      const daysBlocked = item.date_started 
                        ? differenceInDays(new Date(), new Date(item.date_started))
                        : (item.days_blocked || 0);
                      
                      return (
                        <div 
                          key={index} 
                          className={`flex items-start gap-2 p-3 rounded-lg transition-colors ${
                            item.solved ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                          }`}
                        >
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              {item.carried_over && (
                                <Badge variant="outline" className="text-xs">Carried Over</Badge>
                              )}
                              {daysBlocked > 0 && !item.solved && (
                                <Badge variant="destructive" className="text-xs">
                                  {daysBlocked} day{daysBlocked !== 1 ? 's' : ''} blocked
                                </Badge>
                              )}
                              <div className="flex items-center gap-2 ml-auto">
                                <Label htmlFor={`solved-${index}`} className={`text-sm ${item.solved ? 'text-green-700' : 'text-slate-500'}`}>
                                  {item.solved ? 'Solved ✓' : 'Solved?'}
                                </Label>
                                <Switch
                                  id={`solved-${index}`}
                                  checked={item.solved}
                                  onCheckedChange={(checked) => updateArrayItem('blockers', index, 'solved', checked)}
                                  className="data-[state=checked]:bg-blue-600"
                                />
                              </div>
                            </div>
                            <Textarea
                              value={item.description}
                              onChange={(e) => updateArrayItem('blockers', index, 'description', e.target.value)}
                              placeholder="Describe the blocker..."
                              rows={2}
                              disabled={item.solved}
                              className={item.solved ? 'line-through text-slate-400' : ''}
                              autoFocus={index === formData.blockers.length - 1 && !item.description}
                            />
                            {!item.solved && (
                              <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2">
                                  <TeamMemberSelect
                                    value={item.owner || ''}
                                    onChange={(val) => updateArrayItem('blockers', index, 'owner', val)}
                                    teamMembers={project?.stakeholders || []}
                                    placeholder="Owner"
                                    onManageTeam={handleManageTeam}
                                  />
                                  <div className="space-y-1">
                                    <Input
                                      type="date"
                                      value={item.date_started || ''}
                                      onChange={(e) => updateArrayItem('blockers', index, 'date_started', e.target.value)}
                                      className="text-sm"
                                    />
                                    <span className="text-xs text-slate-500">Started</span>
                                  </div>
                                  <div className="space-y-1">
                                    <Input
                                      type="date"
                                      value={item.target_resolution_date || ''}
                                      onChange={(e) => updateArrayItem('blockers', index, 'target_resolution_date', e.target.value)}
                                      className="text-sm"
                                    />
                                    <span className="text-xs text-slate-500">Target Resolution</span>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      checked={item.escalated}
                                      onCheckedChange={(checked) => updateArrayItem('blockers', index, 'escalated', checked)}
                                    />
                                    <Label className="text-sm">Escalated</Label>
                                  </div>
                                  {item.escalated && (
                                    <div className="space-y-1 col-span-2">
                                      <Input
                                        type="date"
                                        value={item.escalation_date || ''}
                                        onChange={(e) => updateArrayItem('blockers', index, 'escalation_date', e.target.value)}
                                        className="text-sm border-red-200"
                                      />
                                      <span className="text-xs text-red-500">Escalation Date</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeArrayItem('blockers', index)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              {/* Enhancement 18: Kudos Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        🎉 Kudos & Recognition
                      </CardTitle>
                      <CardDescription>Public praise for team members or individuals</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addArrayItem('kudos', { person: '', reason: '' })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3" ref={kudosRef}>
                  {formData.kudos.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500">No kudos yet - celebrate your team!</p>
                      {(project?.stakeholders?.length === 0) && (
                        <p className="text-xs text-slate-400 mt-1">
                          Tip: <button 
                            type="button"
                            onClick={() => navigate(`/projects/${projectId}`)}
                            className="text-blue-500 hover:underline"
                          >
                            Add team members
                          </button> in Project Settings for quick selection
                        </p>
                      )}
                    </div>
                  ) : (
                    formData.kudos.map((item, index) => (
                      <div key={index} className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                        <div className="flex-1 space-y-2">
                          <TeamMemberSelect
                            value={item.person}
                            onChange={(val) => updateArrayItem('kudos', index, 'person', val)}
                            teamMembers={project?.stakeholders || []}
                            placeholder="Person or team name"
                            onManageTeam={handleManageTeam}
                            className="bg-white rounded-md"
                          />
                          <Textarea
                            value={item.reason}
                            onChange={(e) => updateArrayItem('kudos', index, 'reason', e.target.value)}
                            placeholder="What they did and why it matters..."
                            rows={2}
                            className="bg-white"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeArrayItem('kudos', index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => updateFormData('notes', e.target.value)}
                    placeholder="Any other comments or notes..."
                    rows={4}
                  />
                </CardContent>
              </Card>

              {/* Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" />
                    Links
                  </CardTitle>
                  <CardDescription>Add links to external documents, spreadsheets, or other resources</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3" ref={linksRef}>
                  {formData.attachments?.length > 0 && (
                    <div className="space-y-2">
                      {formData.attachments.map((attachment, index) => (
                        <div key={attachment.id || index} className="flex gap-2 items-start">
                          <div className="flex-1 grid grid-cols-[1fr_2fr] gap-2">
                            <Input
                              data-link-label-input
                              value={attachment.label}
                              onChange={(e) => {
                                const updated = [...formData.attachments];
                                updated[index] = { ...updated[index], label: e.target.value };
                                updateFormData('attachments', updated);
                              }}
                              placeholder="Label (e.g., Budget Sheet)"
                              maxLength={100}
                              data-testid={`attachment-label-${index}`}
                            />
                            <Input
                              value={attachment.url}
                              onChange={(e) => {
                                const updated = [...formData.attachments];
                                updated[index] = { ...updated[index], url: e.target.value };
                                updateFormData('attachments', updated);
                              }}
                              placeholder="https://..."
                              type="url"
                              data-testid={`attachment-url-${index}`}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-600 shrink-0"
                            onClick={() => {
                              const updated = formData.attachments.filter((_, i) => i !== index);
                              updateFormData('attachments', updated);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {(formData.attachments?.length || 0) < 10 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newAttachment = {
                          id: crypto.randomUUID(),
                          label: '',
                          url: ''
                        };
                        updateFormData('attachments', [...(formData.attachments || []), newAttachment]);
                        // Auto-focus the new link input
                        setTimeout(() => {
                          const inputs = document.querySelectorAll('[data-link-label-input]');
                          const lastInput = inputs[inputs.length - 1];
                          if (lastInput) lastInput.focus();
                        }, 50);
                      }}
                      data-testid="add-attachment-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Link
                    </Button>
                  )}
                  
                  {(formData.attachments?.length || 0) >= 10 && (
                    <p className="text-xs text-slate-500">Maximum 10 attachments allowed</p>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-4">
                {/* Bug 5 fix: Show error near Create Report button */}
                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="flex items-center justify-between">
                  <Button variant="outline" onClick={() => navigate(`/projects/${projectId}`)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting} data-testid="create-report-btn">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isEditMode ? 'Update Report' : 'Create Report'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Column: Live Preview */}
            <div className="lg:sticky lg:top-4 lg:self-start">
              <Card className="overflow-hidden">
                <CardHeader className="bg-slate-900 text-white py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      Live Preview
                    </CardTitle>
                    {isPredictive && project.evm_enabled && (
                      <div className="flex items-center gap-2">
                        <Label htmlFor="show-evm" className="text-xs text-slate-300">Show EVM</Label>
                        <Switch 
                          id="show-evm" 
                          checked={formData.show_evm} 
                          onCheckedChange={(checked) => updateFormData('show_evm', checked)}
                          className="data-[state=checked]:bg-blue-600 scale-75"
                        />
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Preview Header */}
                  <div className="p-4 bg-slate-800 text-white">
                    <h2 className="font-bold text-lg">{project.name}</h2>
                    <p className="text-sm opacity-80">
                      {formData.period_start} to {formData.period_end}
                    </p>
                  </div>

                  {/* Preview Status */}
                  <div className="p-4 border-b">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${statusColors.bg}`}>
                        {formData.status === 'on_track' && <CheckCircle className={`h-6 w-6 ${statusColors.text}`} />}
                        {formData.status === 'at_risk' && <AlertTriangle className={`h-6 w-6 ${statusColors.text}`} />}
                        {formData.status === 'off_track' && <XCircle className={`h-6 w-6 ${statusColors.text}`} />}
                      </div>
                      <div>
                        <Badge className={`${statusColors.bg} ${statusColors.text} border-0`}>
                          {formData.status === 'on_track' && 'On Track'}
                          {formData.status === 'at_risk' && 'At Risk'}
                          {formData.status === 'off_track' && 'Off Track'}
                        </Badge>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                          {formData.status_summary || 'Enter a status summary...'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Preview Metrics */}
                  <div className="p-4 border-b">
                    {isPredictive ? (
                      <div className="space-y-4">
                        {/* Enhancement 16: Show SPI/CPI circles alongside completion */}
                        <div className="flex items-center justify-center gap-6">
                          <PreviewCircle 
                            value={parseFloat(formData.actual_percent) || 0} 
                            label="Done"
                            color="blue"
                          />
                          {/* Enhancement 16: SPI/CPI circles with RAG colors */}
                          {formData.show_evm && evmPreview?.spi && (
                            <PreviewCircle 
                              value={evmPreview.spi * 100} 
                              label="SPI"
                              color={evmPreview.spi >= 0.95 ? 'green' : evmPreview.spi >= 0.85 ? 'amber' : 'red'}
                              isIndex={true}
                              rawValue={evmPreview.spi}
                            />
                          )}
                          {formData.show_evm && evmPreview?.cpi && (
                            <PreviewCircle 
                              value={evmPreview.cpi * 100} 
                              label="CPI"
                              color={evmPreview.cpi >= 0.95 ? 'green' : evmPreview.cpi >= 0.85 ? 'amber' : 'red'}
                              isIndex={true}
                              rawValue={evmPreview.cpi}
                            />
                          )}
                        </div>

                        {/* EVM Metrics Details */}
                        {formData.show_evm && evmPreview && (
                          <div className="grid grid-cols-2 gap-3 mt-4">
                            <EvmMetricCard 
                              label="Schedule Performance Index" 
                              abbr="SPI"
                              value={evmPreview.spi}
                              tooltip="SPI measures schedule efficiency. A value of 1.0 means on schedule, >1.0 is ahead, <1.0 is behind."
                            />
                            <EvmMetricCard 
                              label="Cost Performance Index" 
                              abbr="CPI"
                              value={evmPreview.cpi}
                              tooltip="CPI measures cost efficiency. A value of 1.0 means on budget, >1.0 is under budget, <1.0 is over budget."
                            />
                            <EvmMetricCard 
                              label="Estimate at Completion" 
                              abbr="EAC"
                              value={evmPreview.eac}
                              isCurrency={true}
                              tooltip="EAC is the projected total cost at project completion based on current performance."
                            />
                            <EvmMetricCard 
                              label="Variance at Completion" 
                              abbr="VAC"
                              value={evmPreview.vac}
                              isCurrency={true}
                              isVariance={true}
                              tooltip="VAC is the difference between the budget (BAC) and the estimate at completion (EAC)."
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">
                              Sprint {formData.sprint_number || '?'}
                            </div>
                            <div className="text-xs text-slate-500">Current Sprint</div>
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span>Points Completed</span>
                              <span>{formData.points_completed || 0} / {formData.points_planned || 0}</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-500 h-2 rounded-full transition-all" 
                                style={{ 
                                  width: `${formData.points_planned ? 
                                    Math.min((formData.points_completed / formData.points_planned) * 100, 100) : 0}%` 
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        {formData.sprint_goal && (
                          <div className="p-2 bg-slate-50 rounded text-sm">
                            <span className="text-slate-500">Goal:</span> {formData.sprint_goal}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Preview Accomplishments */}
                  {formData.accomplishments.some(a => a.description) && (
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-sm mb-2 text-slate-700">Accomplishments</h3>
                      <ul className="space-y-1">
                        {formData.accomplishments
                          .filter(a => a.description)
                          .slice(0, 3)
                          .map((a, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="line-clamp-1">{a.description}</span>
                            </li>
                          ))}
                        {formData.accomplishments.filter(a => a.description).length > 3 && (
                          <li className="text-xs text-slate-500">
                            +{formData.accomplishments.filter(a => a.description).length - 3} more...
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Preview Blockers */}
                  {formData.blockers.some(b => b.description && !b.solved) && (
                    <div className="p-4 bg-red-50">
                      <h3 className="font-semibold text-sm mb-2 text-red-700">Blockers</h3>
                      <ul className="space-y-1">
                        {formData.blockers
                          .filter(b => b.description && !b.solved)
                          .slice(0, 2)
                          .map((b, i) => (
                            <li key={i} className="text-sm text-red-600 line-clamp-1">• {b.description}</li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Upcoming */}
                  {formData.upcoming.some(u => u.description) && (
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-sm mb-2 text-slate-700">Upcoming</h3>
                      <ul className="space-y-1">
                        {formData.upcoming
                          .filter(u => u.description)
                          .slice(0, 3)
                          .map((u, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <ArrowRight className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span className="line-clamp-1">{u.description}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Decisions */}
                  {formData.decisions.some(d => d.description && !d.solved) && (
                    <div className="p-4 border-b bg-amber-50">
                      <h3 className="font-semibold text-sm mb-2 text-amber-700">Decisions Needed</h3>
                      <ul className="space-y-1">
                        {formData.decisions
                          .filter(d => d.description && !d.solved)
                          .slice(0, 2)
                          .map((d, i) => (
                            <li key={i} className="text-sm text-amber-600 line-clamp-1">• {d.description}</li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Kudos */}
                  {formData.kudos.some(k => k.person || k.reason) && (
                    <div className="p-4 border-b bg-purple-50">
                      <h3 className="font-semibold text-sm mb-2 text-purple-700">Kudos</h3>
                      <ul className="space-y-1">
                        {formData.kudos
                          .filter(k => k.person || k.reason)
                          .slice(0, 2)
                          .map((k, i) => (
                            <li key={i} className="text-sm text-purple-600">
                              {k.person && <strong>{k.person}</strong>}
                              {k.person && k.reason && ': '}
                              {k.reason}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  {/* Preview Notes */}
                  {formData.notes && formData.notes.trim() && (
                    <div className="p-4 border-b">
                      <h3 className="font-semibold text-sm mb-2 text-slate-700">Notes</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{formData.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Team Management Dialog */}
      <Dialog open={showTeamAlert} onOpenChange={setShowTeamAlert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Manage Team Members
            </DialogTitle>
            <DialogDescription>
              Team members are managed in the Project Settings. Your current report data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              To add or edit team members:
            </p>
            <ol className="mt-2 text-sm text-slate-600 space-y-1 list-decimal list-inside">
              <li>Go to Project Details page</li>
              <li>Click "Edit Project" button</li>
              <li>Scroll to "Team Members" section</li>
              <li>Add your team members with their roles</li>
              <li>Return to this report to continue</li>
            </ol>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTeamAlert(false)}>
              Continue Editing
            </Button>
            <Button onClick={() => {
              // Save draft data so it persists when returning
              localStorage.setItem('reportDraft', JSON.stringify({
                projectId,
                formData,
                timestamp: Date.now()
              }));
              // Save a note that the user was editing this report
              localStorage.setItem('returnToReport', JSON.stringify({
                projectId,
                editReportId,
                timestamp: Date.now()
              }));
              setHasUnsavedChanges(false); // Prevent double warning
              navigate(`/projects/${projectId}`);
            }}>
              Go to Project Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Navigation Warning Dialog */}
      <Dialog open={showNavigationWarning} onOpenChange={setShowNavigationWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => {
              setShowNavigationWarning(false);
              setPendingNavigationPath(null);
            }}>
              Stay on Page
            </Button>
            <Button variant="destructive" onClick={() => {
              localStorage.removeItem('reportDraft');
              setHasUnsavedChanges(false);
              setShowNavigationWarning(false);
              if (pendingNavigationPath) {
                navigate(pendingNavigationPath);
              }
            }}>
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

// Preview circle component - Enhanced for SPI/CPI with RAG colors
const PreviewCircle = ({ value, label, color = 'blue', isIndex = false, rawValue = null }) => {
  const size = 70;
  const circumference = 2 * Math.PI * ((size / 2) - 5);
  // For index values, cap at 100% for visual but show actual value
  const displayPercent = isIndex ? Math.min(value, 100) : value;
  const offset = circumference - (circumference * displayPercent / 100);
  
  const colorMap = {
    blue: { class: 'text-blue-600', stroke: '#3b82f6' },
    green: { class: 'text-emerald-600', stroke: '#22c55e' },
    amber: { class: 'text-amber-500', stroke: '#f59e0b' },
    red: { class: 'text-red-600', stroke: '#dc2626' },
  };
  
  const { class: colorClass, stroke: strokeColor } = colorMap[color] || colorMap.blue;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" style={{ width: size, height: size }}>
        <circle cx={size/2} cy={size/2} r={(size/2) - 5} stroke="#e2e8f0" strokeWidth="5" fill="none" />
        <circle cx={size/2} cy={size/2} r={(size/2) - 5} stroke={strokeColor} strokeWidth="5" fill="none" 
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {isIndex && rawValue !== null ? (
          <span className={`text-sm font-bold ${colorClass}`}>{rawValue.toFixed(2)}</span>
        ) : (
          <span className={`text-sm font-bold ${colorClass}`}>{Math.round(value)}%</span>
        )}
        <span className="text-xs text-slate-500 uppercase">{label}</span>
      </div>
    </div>
  );
};

// EVM Metric Card component with tooltip
const EvmMetricCard = ({ label, abbr, value, isCurrency = false, isVariance = false, tooltip }) => {
  const getColor = () => {
    if (isCurrency && !isVariance) return 'text-slate-700';
    if (isVariance) return value >= 0 ? 'text-emerald-600' : 'text-red-600';
    return value >= 0.95 ? 'text-emerald-600' : value >= 0.85 ? 'text-amber-600' : 'text-red-600';
  };

  const formatValue = () => {
    if (value === null || value === undefined) return '—';
    if (isCurrency) {
      const formatted = Math.abs(value) >= 1000000 
        ? `$${(value / 1000000).toFixed(2)}M` 
        : `$${(value / 1000).toFixed(0)}K`;
      return isVariance && value > 0 ? `+${formatted}` : formatted;
    }
    return value.toFixed(2);
  };

  return (
    <div className="p-2 bg-slate-50 rounded">
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500 truncate">{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3 w-3 text-slate-400 cursor-help flex-shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-xs">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className={`text-lg font-bold ${getColor()}`}>
        {formatValue()}
      </div>
    </div>
  );
};

export default ReportBuilder;
